import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import AnimeCard from '@/components/base/AnimeCard';
import { animeList } from '@/mocks/anime';

export default function AnimeDetailPage() {
  const { id } = useParams<{ id: string }>();
  const anime = animeList.find((a) => a.id === Number(id));

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id]);

  if (!anime) {
    return (
      <>
        <Navbar />
        <main className="pt-20 min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <p className="text-foreground-400 text-lg">未找到该动漫</p>
            <Link to="/" className="text-primary-500 mt-4 inline-block">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedAnimes = animeList
    .filter((a) => a.categorySlug === anime.categorySlug && a.id !== anime.id)
    .slice(0, 6);

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${anime.title} - 全集在线观看`,
    description: anime.longDescription,
    image: anime.cover,
    datePublished: `${anime.year}-01-01`,
    dateModified: anime.updatedAt,
    author: { '@type': 'Person', name: anime.director },
    publisher: { '@type': 'Organization', name: '风车动漫' },
  };

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.fengchedongman.com/' },
      { '@type': 'ListItem', position: 2, name: anime.category, item: `https://www.fengchedongman.com/category/${anime.categorySlug}` },
      { '@type': 'ListItem', position: 3, name: anime.title, item: `https://www.fengchedongman.com/anime/${anime.id}` },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJsonLd) }}
      />
      <Navbar />
      <main className="pt-20">
        <div className="px-4 md:px-6 py-8">
          <div className="max-w-6xl mx-auto">
            <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-6">
              <Link to="/" className="hover:text-primary-500 transition-colors">首页</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <Link to={`/category/${anime.categorySlug}`} className="hover:text-primary-500 transition-colors">
                {anime.category}
              </Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-700 font-medium">{anime.title}</span>
            </nav>

            <div className="flex flex-col md:flex-row gap-8">
              <div className="w-full md:w-72 flex-shrink-0">
                <div className="rounded-lg overflow-hidden">
                  <img
                    src={anime.cover}
                    alt={anime.title}
                    className="w-full aspect-[3/4] object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1">
                <h1 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-3">{anime.title}</h1>

                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span className="px-3 py-1 rounded-full text-xs font-medium bg-primary-500 text-white">
                    {anime.category}
                  </span>
                  <div className="flex items-center gap-1">
                    <i className="ri-star-fill text-accent-400 text-base"></i>
                    <span className="text-lg font-bold text-foreground-900">{anime.rating}</span>
                    <span className="text-xs text-foreground-400">/ 10</span>
                  </div>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                    anime.status === '完结' ? 'bg-secondary-100 text-secondary-700' : 'bg-accent-100 text-accent-700'
                  }`}>
                    {anime.status}
                  </span>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6 p-4 rounded-lg bg-background-100">
                  <div>
                    <p className="text-xs text-foreground-400">年份</p>
                    <p className="text-sm font-medium text-foreground-800">{anime.year}年</p>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-400">季度</p>
                    <p className="text-sm font-medium text-foreground-800">{anime.season}</p>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-400">总集数</p>
                    <p className="text-sm font-medium text-foreground-800">{anime.episodes}集</p>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-400">播放量</p>
                    <p className="text-sm font-medium text-foreground-800">{(anime.views / 10000).toFixed(0)}万</p>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-400">导演</p>
                    <p className="text-sm font-medium text-foreground-800">{anime.director}</p>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-400">制作公司</p>
                    <p className="text-sm font-medium text-foreground-800">{anime.studio}</p>
                  </div>
                </div>

                <div className="mb-6">
                  <h2 className="font-heading text-lg text-foreground-900 mb-2">剧情简介</h2>
                  <p className="text-sm text-foreground-600 leading-relaxed">{anime.longDescription}</p>
                </div>

                <div className="mb-6">
                  <h2 className="font-heading text-lg text-foreground-900 mb-3">标签</h2>
                  <div className="flex flex-wrap gap-2">
                    {anime.tags.map((tag) => (
                      <span key={tag} className="px-3 py-1 rounded-full text-xs bg-secondary-100 text-secondary-700">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="mb-6">
                  <h2 className="font-heading text-lg text-foreground-900 mb-3">剧集列表</h2>
                  <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-2">
                    {Array.from({ length: anime.episodes }, (_, i) => i + 1).map((ep) => (
                      <button
                        key={ep}
                        className="px-3 py-2 text-sm rounded-md bg-background-50 border border-background-200/70 text-foreground-600 hover:border-primary-300 hover:text-primary-500 transition-all whitespace-nowrap cursor-pointer"
                      >
                        第{ep}集
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {relatedAnimes.length > 0 && (
              <section className="mt-14">
                <h2 className="font-heading text-xl text-foreground-900 mb-5">同类推荐</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
                  {relatedAnimes.map((a) => (
                    <AnimeCard key={a.id} anime={a} size="small" />
                  ))}
                </div>
              </section>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}